import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bot, Save, RotateCcw, Loader2, CheckCircle2 } from "lucide-react";

export default function AdminAiSettings() {
  const [cfg, setCfg] = useState(null);
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ai-config");
      setCfg(data);
      setProvider(data.provider);
      setModel(data.model);
    } catch (e) {
      toast.error("Errore caricamento configurazione AI");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!provider || !model) return;
    setBusy(true);
    try {
      await api.put("/admin/ai-config", { provider, model });
      toast.success("Modello AI aggiornato");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    setBusy(true);
    try {
      await api.post("/admin/ai-config/reset");
      toast.success("Ripristinato al default");
      await load();
    } catch (e) {
      toast.error("Errore reset");
    } finally {
      setBusy(false);
    }
  };

  const providers = ["openai", "anthropic", "gemini"];
  const currentChoices = (cfg?.choices || []).filter((c) => c.provider === provider);
  const isDefault = cfg && cfg.provider === cfg.default_provider && cfg.model === cfg.default_model;
  const isDirty = cfg && (provider !== cfg.provider || model !== cfg.model);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-500 gap-2">
        <Loader2 className="animate-spin" size={16} /> Caricamento…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-ai-settings">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Modelli AI</h1>
        <p className="text-sm text-slate-500 mt-1">
          Scegli il modello di intelligenza artificiale utilizzato per generare News, Briefing settimanali e Riepilogo mensile. Le modifiche hanno effetto sulle generazioni successive.
        </p>
      </div>

      <div className="card-elevated p-6 space-y-5 max-w-2xl">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center shadow-md shadow-purple-500/30 flex-shrink-0">
            <Bot className="text-white" size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-slate-900">Modello attivo</div>
            <div className="text-sm text-slate-600 mt-0.5" data-testid="ai-current-model">
              <span className="font-mono text-slate-800">{cfg?.provider}</span>
              <span className="mx-1 text-slate-400">/</span>
              <span className="font-mono text-slate-800">{cfg?.model}</span>
              {isDefault && (
                <span className="ml-2 badge-neutral !bg-emerald-50 !text-emerald-700 text-[10px] inline-flex items-center gap-1">
                  <CheckCircle2 size={10} /> DEFAULT
                </span>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Default di sistema: {cfg?.default_provider} / {cfg?.default_model}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Provider</label>
            <select
              value={provider}
              onChange={(e) => {
                const p = e.target.value;
                setProvider(p);
                const first = (cfg?.choices || []).find((c) => c.provider === p);
                if (first) setModel(first.model);
              }}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="ai-provider-select"
            >
              {providers.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Modello</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white font-mono"
              data-testid="ai-model-select"
            >
              {currentChoices.length === 0 ? (
                <option value={model}>{model} (custom)</option>
              ) : (
                currentChoices.map((c) => (
                  <option key={c.model} value={c.model}>{c.label}</option>
                ))
              )}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Modello personalizzato</label>
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="es. gpt-5.6-sol"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white font-mono"
            data-testid="ai-model-custom-input"
          />
          <div className="text-xs text-slate-400 mt-1">
            Se vuoi provare un modello non in elenco (es. una nuova versione appena rilasciata), inserisci qui il nome esatto.
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={save}
            disabled={!isDirty || busy}
            className="btn-brand flex items-center gap-2 text-sm !py-1.5 !px-3 disabled:opacity-50"
            data-testid="ai-config-save"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            Salva
          </button>
          <button
            onClick={reset}
            disabled={busy || isDefault}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="ai-config-reset"
          >
            <RotateCcw size={14} /> Ripristina default
          </button>
        </div>
      </div>

      <div className="card-elevated p-5 max-w-2xl bg-slate-50/40">
        <div className="text-sm font-semibold text-slate-800 mb-1">Dove viene usato questo modello?</div>
        <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
          <li>Generazione news AI dal web (aggregazione &amp; sintesi)</li>
          <li>Report Mercati Energetici — versione tecnica e divulgativa</li>
          <li>Riepilogo mensile AI dei mercati energetici</li>
        </ul>
        <div className="text-xs text-slate-500 mt-3">
          Suggerimento: modelli più capaci (es. GPT-5.6 Sol, Claude Sonnet 5) producono report di qualità superiore ma consumano più credito.
        </div>
      </div>
    </div>
  );
}
